import React, { useState } from "react";
import Header from "../components/Header";
import SignupModal from "../components/SignupModal";
import Footer from "../components/Footer";
import { 
  FaHandsHelping, 
  FaSeedling, 
  FaUserFriends, 
  FaChalkboardTeacher, 
  FaSchool, 
  FaChartLine, 
  FaLightbulb,
  FaHeart,
  FaShieldAlt,
  FaUsersCog
} from "react-icons/fa";
import { motion } from "framer-motion";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

export default function AboutUs() {
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupRole, setSignupRole] = useState(null);

  const handleOpenSignup = (role) => {
    setSignupRole(role);
    setShowSignupModal(true);
  };

  const handleCloseSignup = () => {
    setShowSignupModal(false);
    setSignupRole(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-blue-50">
      <Header onLoginButtonClick={() => handleOpenSignup(null)} />
      {showSignupModal && (
        <SignupModal
          show={showSignupModal}
          onClose={handleCloseSignup}
          initialRole={signupRole}
        />
      )}
      
      <main className="flex-1 w-full font-sans relative z-0">
        {/* Hero Section */}
        <section className="relative z-0 overflow-hidden bg-gradient-to-r from-indigo-900 to-blue-900 text-white py-20 md:py-28">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHg9IjAiIHk9IjAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNwYXR0ZXJuKSIvPjwvc3ZnPg==')]"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Transforming Autism Care Through <span className="text-blue-300">Innovation</span>
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 mt-6 max-w-3xl mx-auto leading-relaxed">
                Empowering families, therapists, and schools with comprehensive tools and resources to create meaningful, measurable progress in autism care.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Purpose Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 text-sm font-semibold text-blue-600 bg-blue-100 rounded-full mb-4">Our Purpose</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why We Exist</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-blue-400 to-indigo-500 mx-auto"></div>
            </div>
            
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-10"
            >
              <motion.div 
                variants={fadeInUp}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <FaLightbulb className="text-2xl text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Innovation in Care</h3>
                <p className="text-gray-600 text-center">Leveraging technology to create evidence-based solutions that transform how autism care is delivered and experienced.</p>
              </motion.div>

              <motion.div 
                variants={fadeInUp}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <FaHeart className="text-2xl text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Compassionate Support</h3>
                <p className="text-gray-600 text-center">Providing a supportive community and resources that understand the unique challenges of autism care.</p>
              </motion.div>

              <motion.div 
                variants={fadeInUp}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <FaShieldAlt className="text-2xl text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Trusted Platform</h3>
                <p className="text-gray-600 text-center">Maintaining the highest standards of privacy, security, and professional integrity in everything we do.</p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
              >
                <div className="p-8">
                  <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mb-6"></div>
                  <div className="flex items-center mb-6">
                    <div className="p-3 bg-blue-100 rounded-lg mr-4">
                      <FaSeedling className="text-2xl text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Our Vision</h2>
                  </div>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    We envision a world where every individual with autism has access to personalized, effective care that enables them to thrive. Our platform serves as the bridge connecting families with the resources, professionals, and communities they need to make this vision a reality.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
              >
                <div className="p-8">
                  <div className="w-16 h-1 bg-gradient-to-r from-indigo-500 to-purple-600 mb-6"></div>
                  <div className="flex items-center mb-6">
                    <div className="p-3 bg-indigo-100 rounded-lg mr-4">
                      <FaHandsHelping className="text-2xl text-indigo-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Our Mission</h2>
                  </div>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    To empower families, therapists, and educational institutions with innovative tools and resources that transform autism care. We're committed to making evidence-based interventions accessible, measurable, and effective for every individual on the spectrum.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 text-sm font-semibold text-indigo-600 bg-indigo-100 rounded-full mb-4">Our Values</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Guiding Principles</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: <FaUsersCog className="text-2xl text-blue-600" />,
                  title: "Collaboration",
                  description: "We believe in the power of working together with families, professionals, and the community."
                },
                {
                  icon: <FaChartLine className="text-2xl text-green-600" />,
                  title: "Evidence-Based",
                  description: "Our approaches are grounded in research and proven methodologies for effective outcomes."
                },
                {
                  icon: <FaUserFriends className="text-2xl text-purple-600" />,
                  title: "Inclusivity",
                  description: "We celebrate neurodiversity and are committed to creating inclusive environments for all."
                },
                {
                  icon: <FaSchool className="text-2xl text-yellow-600" />,
                  title: "Education",
                  description: "We're dedicated to continuous learning and sharing knowledge with our community."
                }
              ].map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 bg-gray-50 rounded-xl hover:shadow-md transition-shadow duration-300"
                >
                  <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-900 to-blue-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1 text-sm font-semibold text-blue-200 bg-blue-800 bg-opacity-30 rounded-full mb-4">Our Impact</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Making a Difference</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-blue-300 to-indigo-300 mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { number: "500+", label: "Families Supported", icon: <FaUserFriends className="text-3xl" /> },
                { number: "200+", label: "Therapists Connected", icon: <FaChalkboardTeacher className="text-3xl" /> },
                { number: "100+", label: "Inclusive Centers", icon: <FaSchool className="text-3xl" /> },
                { number: "30%", label: "Faster Progress Tracking", icon: <FaChartLine className="text-3xl" /> }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center p-6 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm hover:bg-opacity-20 transition-all duration-300"
                >
                  <div className="text-blue-200 mb-3 flex justify-center">
                    {stat.icon}
                  </div>
                  <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-blue-100 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
